/* eslint-disable react/prop-types, prettier/prettier */
import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { keyframes } from '@emotion/react'
import { Box, Typography, TextField, Paper, Button } from '@mui/material'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import EmailIcon from '@mui/icons-material/Email'
import SmsIcon from '@mui/icons-material/Sms'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import GroupIcon from '@mui/icons-material/Group'
import LoopIcon from '@mui/icons-material/Loop'
import TrackChangesIcon from '@mui/icons-material/TrackChanges'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import SchoolIcon from '@mui/icons-material/School'
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom'
import PersonIcon from '@mui/icons-material/Person'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import FilterListIcon from '@mui/icons-material/FilterList'
import PolylineIcon from '@mui/icons-material/Polyline'
import ArticleIcon from '@mui/icons-material/Article'
import AIService from '../../services/aiService'
import { useToolRun } from '../../helpers/toolRun'
import GlowButton from '../../components/shared/GlowButton'
import CopyButton from '../../components/shared/CopyButton'
import ErrorBanner from '../../components/shared/ErrorBanner'
import {
    ACCENT,
    BRAND,
    CATEGORY,
    CHANNEL_COLORS,
    FONT,
    ICON,
    RADIUS,
    STATUS,
    SURFACE,
    TEXT,
    cardSx,
    pageSx,
    tint,
} from '../../theme/tokens'

const shimmer = keyframes`
  0%{background-position:-400px 0} 100%{background-position:400px 0}
`
const fadeIn = keyframes`
  from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)}
`

// Each receiver group's identity colour (CATEGORY: text-safe on the card and
// on its own tint in both themes).
const PERSONA_META = {
    student: { label: 'Students', Icon: SchoolIcon, color: CATEGORY.orange },
    family: {
        label: 'Family Contacts',
        Icon: FamilyRestroomIcon,
        color: CATEGORY.cyan,
    },
    trainer: { label: 'Trainers', Icon: PersonIcon, color: CATEGORY.green },
    demo_student: {
        label: 'Demo Students',
        Icon: PersonAddIcon,
        color: CATEGORY.amber,
    },
}

const FIELD_LABELS = {
    is_paid_active: 'Paid & Active',
    grade: 'Grade',
    country: 'Country',
    product_id: 'Product',
    batch_id: 'Batch',
    row_status: 'Status',
    batches_region: 'Batches Region',
    demo_session_countries: 'Demo Countries',
    trainer_type: 'Trainer Type',
    time_zone: 'Timezone',
    student_grade: 'Grade',
    attended: 'Attended',
    demo_session_status: 'Session Status',
}

const OP_LABELS = { eq: '=', neq: '≠', contains: '∋', not_contains: '∌' }

function filterValueLabel(f) {
    if (f.field === 'is_paid_active') return f.value ? 'Yes' : 'No'
    if (f.field === 'attended') return f.value ? 'Yes' : 'No'
    if (f.op === 'between') return `${f.min} – ${f.max}`
    return String(f.value)
}

const CHANNELS = [
    {
        key: 'whatsapp',
        label: 'WhatsApp',
        Icon: WhatsAppIcon,
        color: CHANNEL_COLORS.WHATSAPP,
    },
    {
        key: 'email',
        label: 'Email',
        Icon: EmailIcon,
        color: CHANNEL_COLORS.EMAIL,
    },
    { key: 'sms', label: 'SMS', Icon: SmsIcon, color: CHANNEL_COLORS.SMS },
]

const CHANNEL_ICONS = { whatsapp: WhatsAppIcon, email: EmailIcon, sms: SmsIcon }
const CHANNEL_COLOR = {
    whatsapp: CHANNEL_COLORS.WHATSAPP,
    email: CHANNEL_COLORS.EMAIL,
    sms: CHANNEL_COLORS.SMS,
}

function ShimmerBlock({ height = 80, borderRadius = RADIUS.md }) {
    return (
        <Box
            sx={{
                height,
                borderRadius: `${borderRadius}px`,
                overflow: 'hidden',
                background: `linear-gradient(90deg, ${SURFACE.subtle} 25%, ${SURFACE.hover} 50%, ${SURFACE.subtle} 75%)`,
                backgroundSize: '800px 100%',
                animation: `${shimmer} 1.5s infinite linear`,
            }}
        />
    )
}

function BriefSection({ icon: Icon, color, title, children }) {
    return (
        <Paper
            elevation={0}
            sx={{
                ...cardSx,
                p: 2.5,
                animation: `${fadeIn} 0.4s ease`,
            }}
        >
            <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}
            >
                <Box
                    sx={{
                        width: 28,
                        height: 28,
                        borderRadius: `${RADIUS.sm}px`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: tint(color, 0.09),
                    }}
                >
                    <Icon sx={{ fontSize: ICON.md, color }} />
                </Box>
                <Typography variant="overline" component="p" sx={{ color }}>
                    {title}
                </Typography>
            </Box>
            {children}
        </Paper>
    )
}

function ChannelChip({ ch, selected, onClick }) {
    const { label, Icon, color } = ch
    return (
        <Box
            role="button"
            tabIndex={0}
            aria-pressed={selected}
            aria-label={`Channel: ${label}`}
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onClick()
                }
            }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: `${RADIUS.sm}px`,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: selected ? color : SURFACE.border,
                background: selected ? tint(color, 0.08) : SURFACE.card,
                transition: 'all .2s',
                '&:hover': {
                    borderColor: color,
                    background: tint(color, 0.06),
                },
            }}
        >
            <Icon
                sx={{
                    fontSize: ICON.md,
                    color: selected ? color : TEXT.faint,
                }}
            />
            <Typography
                variant="body2"
                sx={{
                    color: selected ? color : TEXT.muted,
                    fontWeight: selected ? 700 : 400,
                }}
            >
                {label}
            </Typography>
        </Box>
    )
}

function RecommendedChannelDisplay({ value }) {
    const parts = (value ?? '').split('+').filter(Boolean)
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {parts.map((ch) => {
                const Icon = CHANNEL_ICONS[ch] || CHANNEL_ICONS.whatsapp
                const color = CHANNEL_COLOR[ch] || CATEGORY.orange
                return (
                    <Box
                        key={ch}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.5,
                            py: 0.5,
                            borderRadius: `${RADIUS.sm}px`,
                            background: tint(color, 0.09),
                            border: `1px solid ${tint(color, 0.27)}`,
                        }}
                    >
                        <Icon sx={{ fontSize: ICON.md, color }} />
                        <Typography
                            variant="body2"
                            sx={{
                                color,
                                fontWeight: 700,
                                textTransform: 'capitalize',
                            }}
                        >
                            {ch}
                        </Typography>
                    </Box>
                )
            })}
        </Box>
    )
}

function AudienceSection({ persona, filterSpec, audienceCount }) {
    const meta = PERSONA_META[persona] || {
        label: persona,
        Icon: GroupIcon,
        color: CATEGORY.orange,
    }
    const { Icon, color, label } = meta
    const filters = filterSpec?.filters ?? []

    const countDisplay = () => {
        if (audienceCount === null || audienceCount === undefined)
            return {
                text: 'Counted at launch',
                hint: 'Matching recipients are tallied when the campaign runs — or enter an estimated audience size above.',
                color: TEXT.muted,
            }
        if (audienceCount === 0)
            return {
                text: '0 matches — review filters',
                color: STATUS.warning.fg,
            }
        return {
            text: audienceCount.toLocaleString(),
            color: STATUS.success.fg,
        }
    }
    const count = countDisplay()

    return (
        <Paper
            elevation={0}
            sx={{
                ...cardSx,
                p: 2.5,
                animation: `${fadeIn} 0.4s ease`,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box
                    sx={{
                        width: 28,
                        height: 28,
                        borderRadius: `${RADIUS.sm}px`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: tint(color, 0.09),
                    }}
                >
                    <FilterListIcon sx={{ fontSize: ICON.md, color }} />
                </Box>
                <Typography variant="overline" component="p" sx={{ color }}>
                    Audience
                </Typography>
            </Box>

            {/* Persona chip */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: `${RADIUS.sm}px`,
                        background: tint(color, 0.08),
                        border: `1px solid ${tint(color, 0.27)}`,
                    }}
                >
                    <Icon sx={{ fontSize: ICON.sm, color }} />
                    <Typography variant="body2" sx={{ fontWeight: 700, color }}>
                        {label}
                    </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: TEXT.faint }}>
                    receiver group
                </Typography>
            </Box>

            {/* Filter chips */}
            {filters.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {filters.map((f, i) => {
                        const fieldLabel = FIELD_LABELS[f.field] || f.field
                        const opLabel = OP_LABELS[f.op] || f.op
                        const valLabel = filterValueLabel(f)
                        const isImplicit = f.field === 'is_paid_active'
                        return (
                            <Box
                                key={i}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 1.25,
                                    py: 0.4,
                                    borderRadius: `${RADIUS.sm}px`,
                                    background: SURFACE.subtle,
                                    border: `1px solid ${SURFACE.border}`,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: TEXT.muted,
                                        fontWeight: 600,
                                    }}
                                >
                                    {fieldLabel}
                                </Typography>
                                {!isImplicit && (
                                    <>
                                        <Typography
                                            variant="caption"
                                            sx={{ color: TEXT.faint }}
                                        >
                                            {opLabel}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: TEXT.primary,
                                                fontWeight: 700,
                                            }}
                                        >
                                            {valLabel}
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        )
                    })}
                </Box>
            )}

            {/* Live count */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography
                    variant="h1"
                    component="p"
                    sx={{ color: count.color, lineHeight: 1 }}
                >
                    {count.text}
                </Typography>
                {count.hint && (
                    <Typography
                        variant="caption"
                        component="p"
                        sx={{ color: TEXT.faint }}
                    >
                        {count.hint}
                    </Typography>
                )}
                {audienceCount > 0 && (
                    <Typography variant="body2" sx={{ color: TEXT.faint }}>
                        matched
                    </Typography>
                )}
            </Box>
        </Paper>
    )
}

function UnmappedIntentSection({ items }) {
    if (!items || items.length === 0) return null
    return (
        <Paper
            elevation={0}
            sx={{
                background: STATUS.warning.bg,
                border: `1px solid ${STATUS.warning.border}`,
                borderRadius: `${RADIUS.lg}px`,
                p: 2.5,
                animation: `${fadeIn} 0.4s ease`,
            }}
        >
            <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}
            >
                <WarningAmberIcon
                    sx={{ fontSize: ICON.md, color: STATUS.warning.fg }}
                />
                <Typography
                    variant="overline"
                    component="p"
                    sx={{ color: STATUS.warning.fg }}
                >
                    Partially Unmapped Goal
                </Typography>
            </Box>
            <Typography
                variant="caption"
                component="p"
                sx={{ color: TEXT.faint, mb: 1.25 }}
            >
                These parts of your goal couldn&apos;t be mapped to a queryable
                filter — the audience above excludes them:
            </Typography>
            {items.map((item, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                    <Typography
                        variant="caption"
                        sx={{ color: STATUS.warning.fg, mt: '1px' }}
                    >
                        •
                    </Typography>
                    <Typography variant="body2" sx={{ color: TEXT.secondary }}>
                        {item}
                    </Typography>
                </Box>
            ))}
        </Paper>
    )
}

export default function CampaignBrief() {
    const navigate = useNavigate()
    const location = useLocation()
    const [goal, setGoal] = useState('')
    const [selectedChannels, setSelectedChannels] = useState(['whatsapp'])
    const [audienceSize, setAudienceSize] = useState('')
    const [loading, setLoading] = useState(false)
    const [brief, setBrief] = useState(null)
    const [error, setError] = useState('')
    const run = useToolRun('campaign-brief')

    useEffect(() => {
        // Reset outputs so re-runs (navigation with new state) behave predictably
        setBrief(null)
        setError('')
        const saved = run.recall()
        if (saved) {
            setGoal(saved.goal ?? '')
            setSelectedChannels(saved.selectedChannels)
            setAudienceSize(saved.audienceSize ?? '')
            setBrief(saved.brief)
            return
        }
        const state = location.state
        if (!state) return
        if (state.goal) setGoal(state.goal)
        if (
            state.channels &&
            Array.isArray(state.channels) &&
            state.channels.length > 0
        )
            setSelectedChannels(state.channels)
        if (state.audience_size) setAudienceSize(String(state.audience_size))
    }, [location.state])

    const toggleChannel = (key) => {
        setSelectedChannels((prev) =>
            prev.includes(key)
                ? prev.length > 1
                    ? prev.filter((c) => c !== key)
                    : prev
                : [...prev, key]
        )
    }

    const handleGenerate = async () => {
        if (!goal.trim()) return
        setLoading(true)
        setError('')
        setBrief(null)
        run.forget()
        try {
            const res = await AIService.campaignBrief({
                goal,
                channels: selectedChannels,
                audience_size: audienceSize
                    ? parseInt(audienceSize, 10)
                    : undefined,
            })
            setBrief(res.data.result)
            if (res.data.result)
                run.remember({
                    goal,
                    selectedChannels,
                    audienceSize,
                    brief: res.data.result,
                })
        } catch (e) {
            setError(
                e?.response?.data?.message ||
                    'Failed to generate brief. Please try again.'
            )
        } finally {
            setLoading(false)
        }
    }

    // A faint dot grid in the text colour, so it shows on either theme.
    const dotGrid = {
        backgroundImage: `radial-gradient(${tint(
            TEXT.primary,
            0.07
        )} 1px, transparent 1px)`,
        backgroundSize: '28px 28px',
    }

    return (
        <Box
            sx={{
                ...pageSx,
                ...dotGrid,
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: -150,
                    right: -100,
                    width: 600,
                    height: 600,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${tint(
                        BRAND.orange,
                        0.1
                    )} 0%, transparent 70%)`,
                    pointerEvents: 'none',
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: -80,
                    left: -60,
                    width: 400,
                    height: 400,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${tint(
                        CATEGORY.cyan,
                        0.07
                    )} 0%, transparent 70%)`,
                    pointerEvents: 'none',
                }}
            />

            <Box
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    maxWidth: 900,
                    mx: 'auto',
                    p: { xs: 3, md: 5 },
                }}
            >
                <Box sx={{ mb: 4 }}>
                    <Typography
                        variant="h2"
                        component="h5"
                        sx={{ color: TEXT.primary }}
                    >
                        Campaign Brief
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{ color: TEXT.faint, mt: 0.5 }}
                    >
                        Describe your goal — get a full campaign strategy in
                        seconds.
                    </Typography>
                </Box>

                <Paper elevation={0} sx={{ ...cardSx, p: 3, mb: 3 }}>
                    <Typography
                        variant="overline"
                        component="p"
                        sx={{ color: ACCENT.text, mb: 1.5 }}
                    >
                        Campaign Goal
                    </Typography>
                    <TextField
                        multiline
                        rows={4}
                        fullWidth
                        value={goal}
                        onChange={(e) => setGoal(e.target.value)}
                        inputProps={{ 'aria-label': 'Campaign goal' }}
                        placeholder="e.g. Re-engage students who have missed 3 or more classes in the last 2 weeks and encourage them to resume their batch"
                        sx={{ mb: 3 }}
                    />

                    <Box
                        sx={{
                            display: 'flex',
                            gap: 3,
                            flexWrap: 'wrap',
                            alignItems: 'flex-start',
                        }}
                    >
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                            <Typography
                                variant="caption"
                                component="p"
                                sx={{ color: TEXT.faint, mb: 1 }}
                            >
                                Available channels
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                }}
                            >
                                {CHANNELS.map((ch) => (
                                    <ChannelChip
                                        key={ch.key}
                                        ch={ch}
                                        selected={selectedChannels.includes(
                                            ch.key
                                        )}
                                        onClick={() => toggleChannel(ch.key)}
                                    />
                                ))}
                            </Box>
                        </Box>
                        <Box sx={{ minWidth: 160 }}>
                            <Typography
                                variant="caption"
                                component="p"
                                sx={{ color: TEXT.faint, mb: 1 }}
                            >
                                Audience size (optional)
                            </Typography>
                            <TextField
                                size="small"
                                value={audienceSize}
                                onChange={(e) =>
                                    setAudienceSize(
                                        e.target.value.replace(/\D/g, '')
                                    )
                                }
                                placeholder="e.g. 5000"
                                inputProps={{ 'aria-label': 'Audience size' }}
                            />
                        </Box>
                    </Box>
                </Paper>

                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                    <GlowButton
                        onClick={handleGenerate}
                        disabled={!goal.trim()}
                        loading={loading}
                    >
                        Generate Campaign Brief
                    </GlowButton>
                </Box>

                {error && (
                    <Box sx={{ mb: 3 }}>
                        <ErrorBanner message={error} />
                    </Box>
                )}

                {loading && (
                    <Box sx={{ display: 'grid', gap: 2 }}>
                        <ShimmerBlock height={60} />
                        {[120, 100, 160, 100, 80].map((h, i) => (
                            <ShimmerBlock key={i} height={h} />
                        ))}
                    </Box>
                )}

                {brief && (
                    <Box sx={{ display: 'grid', gap: 2 }}>
                        {/* Header */}
                        <Paper
                            elevation={0}
                            sx={{
                                background: `linear-gradient(135deg, ${
                                    ACCENT.bg15
                                }, ${tint(CATEGORY.cyan, 0.08)})`,
                                border: `1px solid ${ACCENT.border30}`,
                                borderRadius: `${RADIUS.lg}px`,
                                p: 3,
                                animation: `${fadeIn} 0.3s ease`,
                            }}
                        >
                            <Typography
                                variant="h2"
                                component="p"
                                sx={{ color: TEXT.primary, lineHeight: 1.2 }}
                            >
                                {brief.campaign_name}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{ color: TEXT.muted, mt: 1 }}
                            >
                                {brief.objective}
                            </Typography>
                        </Paper>

                        {/* Audience + unmapped */}
                        {brief.persona && brief.filter_spec && (
                            <AudienceSection
                                persona={brief.persona}
                                filterSpec={brief.filter_spec}
                                audienceCount={brief.audience_count}
                            />
                        )}
                        <UnmappedIntentSection items={brief.unmapped_intent} />

                        {/* Recommended channel */}
                        {brief.recommended_channel && (
                            <BriefSection
                                icon={WhatsAppIcon}
                                color={CATEGORY.orange}
                                title="Recommended Channel"
                            >
                                <RecommendedChannelDisplay
                                    value={brief.recommended_channel}
                                />
                            </BriefSection>
                        )}

                        <BriefSection
                            icon={LightbulbIcon}
                            color={CATEGORY.amber}
                            title="Template Suggestion"
                        >
                            <Box
                                sx={{
                                    background: SURFACE.sunken,
                                    borderRadius: `${RADIUS.md}px`,
                                    p: 2,
                                    border: `1px solid ${SURFACE.border}`,
                                    position: 'relative',
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                    }}
                                >
                                    <CopyButton
                                        text={brief.template_suggestion ?? ''}
                                    />
                                </Box>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: TEXT.secondary,
                                        whiteSpace: 'pre-wrap',
                                        fontFamily: FONT.mono,
                                        lineHeight: 1.7,
                                        pr: 4,
                                    }}
                                >
                                    {brief.template_suggestion}
                                </Typography>
                            </Box>
                        </BriefSection>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: '1fr 1fr',
                                },
                                gap: 2,
                            }}
                        >
                            <BriefSection
                                icon={AccessTimeIcon}
                                color={CATEGORY.green}
                                title="Best Send Time"
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: TEXT.secondary,
                                        lineHeight: 1.6,
                                    }}
                                >
                                    {brief.send_time}
                                </Typography>
                            </BriefSection>

                            <BriefSection
                                icon={LoopIcon}
                                color={CATEGORY.orange}
                                title="Follow-up Strategy"
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: TEXT.secondary,
                                        lineHeight: 1.6,
                                    }}
                                >
                                    {brief.follow_up}
                                </Typography>
                            </BriefSection>
                        </Box>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    sm: '1fr 1fr',
                                },
                                gap: 2,
                            }}
                        >
                            <BriefSection
                                icon={TrackChangesIcon}
                                color={CATEGORY.cyan}
                                title="Success Metrics"
                            >
                                {(brief.success_metrics ?? []).map((m, i) => (
                                    <Box
                                        key={i}
                                        sx={{
                                            display: 'flex',
                                            gap: 1,
                                            mb: 0.75,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: CATEGORY.cyan,
                                                mt: '1px',
                                            }}
                                        >
                                            ✓
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: TEXT.secondary }}
                                        >
                                            {m}
                                        </Typography>
                                    </Box>
                                ))}
                            </BriefSection>

                            <BriefSection
                                icon={TrackChangesIcon}
                                color={CATEGORY.amber}
                                title="Estimated Impact"
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: TEXT.secondary,
                                        lineHeight: 1.6,
                                    }}
                                >
                                    {brief.estimated_impact}
                                </Typography>
                            </BriefSection>
                        </Box>

                        {/* CTA row */}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                                mt: 2,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<PolylineIcon />}
                                // The new-pipeline wizard, started from the
                                // brief's name and objective. It used to go to
                                // "/" (where the pipeline list once lived), so
                                // the button landed on the home page.
                                // `duplicate` is the wizard's prefill hand-off: a
                                // pipeline-shaped draft.
                                onClick={() =>
                                    navigate('/pipelines/new', {
                                        state: {
                                            duplicate: {
                                                pipeline_name:
                                                    brief.campaign_name ?? '',
                                                comment: brief.objective ?? '',
                                            },
                                        },
                                    })
                                }
                            >
                                Create Pipeline
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<ArticleIcon />}
                                onClick={() =>
                                    navigate('/templates', {
                                        state: {
                                            channel: brief.recommended_channel,
                                        },
                                    })
                                }
                            >
                                Browse Templates
                            </Button>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    )
}
